import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { formatAdvanceNumber } from '../utils/formatters';
import Tooltip from './ui/simple-tooltip';
import { 
  RefreshCwIcon,
  FileTextIcon,
  ClockIcon,
  CheckCircleIcon,
  CreditCardIcon,
  XCircleIcon,
  LockIcon,
  TrendingUpIcon,
  UserIcon,
  CalendarIcon,
  FilterIcon,
  SearchIcon,
  XIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AllRequests = () => {
  const { user } = useAuth();
  const [requestsData, setRequestsData] = useState(null);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Currency filter states
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [baseCurrency, setBaseCurrency] = useState('TRY');
  const [isBaseCurrencyView, setIsBaseCurrencyView] = useState(true); // Track if "Temel Para Birimi" view is selected
  
  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    dateFrom: '',
    dateTo: '',
    minAmount: '',
    maxAmount: '',
    requester: '',
    project: '',
    category: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Fetch available currencies
  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${API}/advance-requests/currencies`);
      setAvailableCurrencies(response.data.all_currencies || []);
      setBaseCurrency(response.data.base_currency || 'TRY');
      // Set base currency as default selected with base view
      setSelectedCurrency(response.data.base_currency || 'TRY');
      setIsBaseCurrencyView(true); // Default to base currency view (show all)
    } catch (error) {
      console.error('Error fetching currencies:', error);
      toast.error('Para birimleri yüklenirken hata oluştu');
    }
  };

  // Fetch all requests data
  const fetchAllRequests = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/advance-requests/all`);
      setRequestsData(response.data);
      
      // Sort requests by advance_number (highest first) to ensure newest is on top
      const sortedRequests = (response.data.requests || []).sort((a, b) => {
        // Extract numeric part from advance numbers (e.g., "0001" -> 1, "0005" -> 5)
        const numA = parseInt(a.advance_number?.replace(/\D/g, '') || '0');
        const numB = parseInt(b.advance_number?.replace(/\D/g, '') || '0');
        return numB - numA; // Descending order (highest number first)
      });
      
      setFilteredRequests(sortedRequests);
    } catch (error) {
      console.error('Error fetching all requests:', error);
      toast.error('Tüm talepler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  const applyFilters = () => {
    if (!requestsData?.requests) return;

    let filtered = [...requestsData.requests];

    // Currency filter - CRITICAL: 
    // If isBaseCurrencyView is TRUE, show ALL requests (no filtering)
    // If isBaseCurrencyView is FALSE, filter by selectedCurrency
    if (!isBaseCurrencyView && selectedCurrency) {
      filtered = filtered.filter(req => req.currency === selectedCurrency);
    }
    // If isBaseCurrencyView === true, show all requests (no filter)

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(req => req.status === filters.status);
    }

    // Date range filter
    if (filters.dateFrom) {
      filtered = filtered.filter(req => {
        const requestDate = new Date(req.request_date);
        const fromDate = new Date(filters.dateFrom);
        return requestDate >= fromDate;
      });
    }
    if (filters.dateTo) {
      filtered = filtered.filter(req => {
        const requestDate = new Date(req.request_date);
        const toDate = new Date(filters.dateTo);
        return requestDate <= toDate;
      });
    }

    // Amount range filter
    if (filters.minAmount) {
      filtered = filtered.filter(req => req.amount >= parseFloat(filters.minAmount));
    }
    if (filters.maxAmount) {
      filtered = filtered.filter(req => req.amount <= parseFloat(filters.maxAmount));
    }

    // Requester filter
    if (filters.requester) {
      filtered = filtered.filter(req => 
        req.requester_name.toLowerCase().includes(filters.requester.toLowerCase())
      );
    }

    // Project filter
    if (filters.project) {
      filtered = filtered.filter(req => 
        req.project.toLowerCase().includes(filters.project.toLowerCase())
      );
    }

    // Category filter
    if (filters.category) {
      filtered = filtered.filter(req => 
        req.category.toLowerCase().includes(filters.category.toLowerCase())
      );
    }

    // Sort by advance_number (highest first)
    filtered.sort((a, b) => {
      const numA = parseInt(a.advance_number?.replace(/\D/g, '') || '0');
      const numB = parseInt(b.advance_number?.replace(/\D/g, '') || '0');
      return numB - numA; // Descending order (highest number first)
    });

    setFilteredRequests(filtered);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      status: '',
      dateFrom: '',
      dateTo: '',
      minAmount: '',
      maxAmount: '',
      requester: '',
      project: '',
      category: ''
    });
    
    // Sort by advance_number (highest first) when clearing filters
    const sortedRequests = (requestsData?.requests || []).sort((a, b) => {
      const numA = parseInt(a.advance_number?.replace(/\D/g, '') || '0');
      const numB = parseInt(b.advance_number?.replace(/\D/g, '') || '0');
      return numB - numA; // Descending order (highest number first)
    });
    
    setFilteredRequests(sortedRequests);
  };

  // Refresh data when component becomes visible
  useEffect(() => {
    fetchCurrencies();
    fetchAllRequests();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, requestsData, selectedCurrency, isBaseCurrencyView]);

  // Format currency
  // Helper function for currency symbols
  const getCurrencySymbol = (currencyCode) => {
    const symbols = {
      'TRY': '₺', 'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'CHF': 'Fr',
      'AED': 'د.إ', 'SAR': '﷼', 'KWD': 'د.ك', 'QAR': '﷼', 'BHD': '.د.ب',
      'OMR': '﷼', 'JOD': 'د.ا', 'CNY': '¥', 'INR': '₹', 'RUB': '₽',
      'BRL': 'R$', 'MXN': '$', 'CAD': 'C$', 'AUD': 'A$', 'NZD': 'NZ$',
      'SGD': 'S$', 'HKD': 'HK$', 'KRW': '₩', 'SEK': 'kr', 'NOK': 'kr',
      'DKK': 'kr', 'PLN': 'zł', 'CZK': 'Kč', 'HUF': 'Ft', 'RON': 'lei',
      'BGN': 'лв', 'HRK': 'kn', 'ILS': '₪', 'ZAR': 'R', 'THB': '฿',
      'MYR': 'RM', 'IDR': 'Rp', 'PHP': '₱', 'VND': '₫', 'PKR': '₨',
      'BDT': '৳', 'LKR': '₨', 'NPR': '₨', 'AFN': '؋', 'IRR': '﷼',
      'IQD': 'ع.د', 'SYP': '£', 'LBP': 'ل.ل', 'EGP': '£', 'MAD': 'د.م.',
      'TND': 'د.ت', 'DZD': 'د.ج', 'LYD': 'ل.د', 'ALL': 'L', 'ANG': 'ƒ', 'AWG': 'ƒ'
    };
    return symbols[currencyCode] || currencyCode;
  };

  const formatCurrency = (amount, currency = 'USD') => {
    const formatted = amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 });
    return `${formatted} ${getCurrencySymbol(currency)}`;
  };

  // Format date (date only for display, full datetime for tooltip)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    // Check if valid date
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('tr-TR', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit'
    });
  };

  // Format full datetime for tooltip
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString('tr-TR', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Calculate statistics from filtered requests
  const calculateStats = () => {
    if (!filteredRequests || filteredRequests.length === 0) {
      return {
        onaylanan: 0,
        odenen: 0,
        kapama_bekleyen: 0,
        kapatilan: 0,
        bakiye: 0
      };
    }

    const onaylananReqs = filteredRequests.filter(req => ['approved', 'paid', 'finance_pending', 'closed'].includes(req.status));
    const onaylanan = isBaseCurrencyView
      ? onaylananReqs.reduce((sum, req) => sum + (req.converted_approved_amount || req.converted_amount || 0), 0)
      : onaylananReqs.reduce((sum, req) => sum + (req.approved_amount || req.amount || 0), 0);
      
    const odenenReqs = filteredRequests.filter(req => ['paid', 'finance_pending', 'closed'].includes(req.status));
    const odenen = isBaseCurrencyView
      ? odenenReqs.reduce((sum, req) => sum + (req.converted_approved_amount || req.converted_amount || 0), 0)
      : odenenReqs.reduce((sum, req) => sum + (req.approved_amount || req.amount || 0), 0);
      
    const kapamaBekleyenReqs = filteredRequests.filter(req => req.status === 'finance_pending');
    const kapama_bekleyen = isBaseCurrencyView
      ? kapamaBekleyenReqs.reduce((sum, req) => sum + (req.converted_approved_amount || req.converted_amount || 0), 0)
      : kapamaBekleyenReqs.reduce((sum, req) => sum + (req.approved_amount || req.amount || 0), 0);
      
    const kapatilanReqs = filteredRequests.filter(req => req.status === 'closed');
    const kapatilan = isBaseCurrencyView
      ? kapatilanReqs.reduce((sum, req) => sum + (req.converted_closed_amount || 0), 0)
      : kapatilanReqs.reduce((sum, req) => sum + (req.converted_closed_amount || 0), 0);
      
    const bakiye = kapatilan - odenen;

    return {
      onaylanan,
      odenen,
      kapama_bekleyen,
      kapatilan,
      bakiye
    };
  };

  const stats = calculateStats();

  // Get status badge styling - UPDATED STATUS FLOW
  const getStatusBadge = (request) => {
    const status = request.status;
    const closingStatus = request.closing_status;
    
    // NEW STATUS FLOW:
    // 1. pending → Onay Bekliyor
    // 2. approved → Ödeme Bekliyor
    // 3. paid → Kapama Bekliyor
    // 4. closing_status='submitted' → Finans Onayında
    // 5. closed → Kapatıldı
    
    // Rejected status
    if (status === 'rejected') {
      return (
        <Badge className="bg-red-100 text-red-800 border-red-200">
          <XCircleIcon className="w-3 h-3 mr-1" />
          Reddedildi
        </Badge>
      );
    }
    
    // Closed status - FINAL STATE
    if (status === 'closed') {
      return (
        <Badge className="bg-gray-100 text-gray-800 border-gray-200">
          <LockIcon className="w-3 h-3 mr-1" />
          Kapatıldı
        </Badge>
      );
    }
    
    // Paid status - Check closing_status for sub-states
    if (status === 'paid') {
      // If submitted to finance, show "Finans Onayında"
      if (closingStatus === 'submitted') {
        return (
          <Badge className="bg-orange-100 text-orange-800 border-orange-200">
            <ClockIcon className="w-3 h-3 mr-1" />
            Finans Onayında
          </Badge>
        );
      }
      
      if (closingStatus === 'rejected') {
        return (
          <Badge className="bg-red-100 text-red-800 border-red-200">
            <XCircleIcon className="w-3 h-3 mr-1" />
            Finans Reddetti
          </Badge>
        );
      }
      
      // If paid but no closing_status or not submitted, show "Kapama Bekliyor"
      return (
        <Badge className="bg-blue-100 text-blue-800 border-blue-200">
          <CreditCardIcon className="w-3 h-3 mr-1" />
          Kapama Bekliyor
        </Badge>
      );
    }
    
    // Approved status - Check if partial approval
    if (status === 'approved') {
      // Check if partially approved (approved_amount < requested amount)
      const isPartialApproval = request.approved_amount && request.amount && 
                                 request.approved_amount < request.amount;
      
      if (isPartialApproval) {
        return (
          <Badge className="bg-orange-100 text-orange-800 border-orange-200">
            <TrendingUpIcon className="w-3 h-3 mr-1" />
            Kısmi Onaylandı
          </Badge>
        );
      }
      
      // Full approval
      return (
        <Badge className="bg-purple-100 text-purple-800 border-purple-200">
          <CheckCircleIcon className="w-3 h-3 mr-1" />
          Ödeme Bekliyor
        </Badge>
      );
    }
    
    // Pending status - Show "Onay Bekliyor"
    if (status === 'pending') {
      return (
        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
          <ClockIcon className="w-3 h-3 mr-1" />
          Onay Bekliyor
        </Badge>
      );
    }
    
    // Priority 6: Finance pending status
    if (status === 'finance_pending') {
      return (
        <Badge className="bg-orange-100 text-orange-800 border-orange-200">
          <ClockIcon className="w-3 h-3 mr-1" />
          Finans Onayında
        </Badge>
      );
    }
    
    // Fallback: Show status in Turkish or unknown
    const turkishStatusMap = {
      'advance_closing': 'Kapama Bekliyor',
      'submitted': 'Gönderildi',
      'under_review': 'İnceleniyor',
      'draft': 'Taslak'
    };
    
    const displayText = turkishStatusMap[status] || status || 'Bilinmeyen';
    
    return (
      <Badge className="bg-gray-200 text-gray-700 border-gray-300">
        <ClockIcon className="w-3 h-3 mr-1" />
        {displayText}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCwIcon className="w-6 h-6 animate-spin mr-2" />
        <span>Tüm talepler yükleniyor...</span>
      </div>
    );
  }

  if (!requestsData || requestsData.requests.length === 0) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tüm Talepler</h1>
            <p className="text-gray-600 mt-1">Tüm avans taleplerinin durumu ve geçmişi</p>
          </div>
        </div>

        {/* Empty State */}
        <Card className="p-12 text-center">
          <FileTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz avans talebi yok</h3>
          <p className="text-gray-500">
            İlk avans talebinizi oluşturduktan sonra tüm talepleriniz burada görünecektir.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Tüm Talepler</h1>
          <p className="text-gray-600 mt-1 text-xs sm:text-sm">
            <UserIcon className="w-4 h-4 inline mr-1" />
            Tüm avans taleplerinin durumu ve geçmişi
          </p>
        </div>
        
        {/* Currency Selector Dropdown */}
        <div className="relative">
          <select
            value={isBaseCurrencyView ? `${baseCurrency}_base` : selectedCurrency}
            onChange={(e) => {
              const value = e.target.value;
              if (value.endsWith('_base')) {
                // Selected "TRY (Temel Para Birimi)" - show all requests
                const currency = value.replace('_base', '');
                setSelectedCurrency(currency);
                setIsBaseCurrencyView(true);
              } else {
                // Selected normal currency (TRY, EUR, USD) - filter by that currency
                setSelectedCurrency(value);
                setIsBaseCurrencyView(false);
              }
            }}
            className="w-full sm:min-w-[200px] px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
          >
            {/* First option: Base Currency View (shows all requests) */}
            <option value={`${baseCurrency}_base`}>
              {baseCurrency} (Temel Para Birimi)
            </option>
            
            {/* Other options: Individual currencies (filters by currency) */}
            {availableCurrencies.map((currency) => (
              <option key={currency} value={currency}>
                {currency}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Count Statistics Cards - Same as Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {/* 1. Toplam Talepler */}
        <Card className="p-4 text-center hover:shadow-md transition-shadow">
          <div className="text-sm font-medium text-gray-600 mb-1">Toplam Talepler</div>
          <div className="text-3xl font-bold text-gray-900">
            {filteredRequests.length}
          </div>
        </Card>
        
        {/* 2. Onay Bekliyor */}
        <Card className="p-4 text-center hover:shadow-md transition-shadow">
          <div className="text-sm font-medium text-gray-600 mb-1">Onay Bekliyor</div>
          <div className="text-3xl font-bold text-yellow-600">
            {filteredRequests.filter(req => req.status === 'pending').length}
          </div>
        </Card>
        
        {/* 3. Onaylandı */}
        <Card className="p-4 text-center hover:shadow-md transition-shadow">
          <div className="text-sm font-medium text-gray-600 mb-1">Onaylandı</div>
          <div className="text-3xl font-bold text-green-600">
            {filteredRequests.filter(req => req.status === 'approved').length}
          </div>
        </Card>
        
        {/* 4. Reddedildi */}
        <Card className="p-4 text-center hover:shadow-md transition-shadow">
          <div className="text-sm font-medium text-gray-600 mb-1">Reddedildi</div>
          <div className="text-3xl font-bold text-red-600">
            {filteredRequests.filter(req => req.status === 'rejected').length}
          </div>
        </Card>
        
        {/* 5. Finans Onayında */}
        <Card className="p-4 text-center hover:shadow-md transition-shadow">
          <div className="text-sm font-medium text-gray-600 mb-1">Finans Onayında</div>
          <div className="text-3xl font-bold text-orange-600">
            {filteredRequests.filter(req => req.status === 'finance_pending').length}
          </div>
        </Card>
        
        {/* 6. Kapandı */}
        <Card className="p-4 text-center hover:shadow-md transition-shadow">
          <div className="text-sm font-medium text-gray-600 mb-1">Kapandı</div>
          <div className="text-3xl font-bold text-gray-600">
            {filteredRequests.filter(req => req.status === 'closed').length}
          </div>
        </Card>
      </div>

      {/* Amount Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center text-white text-xl font-bold mr-4">
              ✅
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-600 font-medium whitespace-nowrap">Onaylanan</p>
              <p className="text-base font-bold text-gray-900">
                {formatCurrency(stats.onaylanan, selectedCurrency)}
              </p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center text-white text-xl font-bold mr-4">
              💰
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-600 font-medium whitespace-nowrap">Ödenen</p>
              <p className="text-base font-bold text-gray-900">
                {formatCurrency(stats.odenen, selectedCurrency)}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center text-white text-xl font-bold mr-4">
              ⏳
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-600 font-medium whitespace-nowrap">Kapama Bekleyen</p>
              <p className="text-base font-bold text-gray-900">
                {formatCurrency(stats.kapama_bekleyen, selectedCurrency)}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center text-white text-xl font-bold mr-4">
              🔒
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-600 font-medium whitespace-nowrap">Kapatılan</p>
              <p className="text-base font-bold text-gray-900">
                {formatCurrency(stats.kapatilan, selectedCurrency)}
              </p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600 text-xl font-bold mr-4">
              ⚖️
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-600 font-medium whitespace-nowrap">Bakiye</p>
              <p className={`text-base font-bold ${stats.bakiye < 0 ? 'text-red-600' : 'text-green-600'}`}>
                {(stats.bakiye < 0 ? '-' : '+')}{formatCurrency(Math.abs(stats.bakiye), selectedCurrency)}
              </p>
              {stats.bakiye !== 0 && (
                <p className={`text-xs font-medium mt-1 ${stats.bakiye < 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {stats.bakiye < 0 ? 'Şirkete Borçlusunuz' : 'Şirketten Alacaklısınız'}
                </p>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Collapsible Filter Bar */}
      <Card>
        {/* Filter Header */}
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setShowFilters(!showFilters)}
        >
          <div className="flex items-center gap-2">
            <FilterIcon className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Filtreler</h3>
            <span className="text-sm text-gray-500">
              ({filteredRequests.length} / {requestsData?.requests?.length || 0} talep)
            </span>
          </div>
          <div className="flex items-center gap-2">
            {Object.values(filters).some(value => value !== '') && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  clearFilters();
                }}
                variant="outline"
                size="sm"
                className="gap-1"
              >
                <XIcon className="w-4 h-4" />
                Temizle
              </Button>
            )}
            {showFilters ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-600" />
            )}
          </div>
        </div>

        {/* Collapsible Filter Content */}
        {showFilters && (
          <div className="border-t border-gray-200 p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Durum</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({...prev, status: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tüm durumlar</option>
                  <option value="pending">Onay Bekliyor</option>
                  <option value="approved">Onaylandı</option>
                  <option value="paid">Ödendi</option>
                  <option value="closed">Kapatıldı</option>
                  <option value="rejected">Reddedildi</option>
                </select>
              </div>

              {/* Date From */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Başlangıç Tarihi</label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters(prev => ({...prev, dateFrom: e.target.value}))}
                  className="modern-date-picker"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bitiş Tarihi</label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters(prev => ({...prev, dateTo: e.target.value}))}
                  className="modern-date-picker"
                />
              </div>

              {/* Requester */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Talep Eden</label>
                <Input
                  type="text"
                  placeholder="İsim ara..."
                  value={filters.requester}
                  onChange={(e) => setFilters(prev => ({...prev, requester: e.target.value}))}
                />
              </div>

              {/* Min Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min. Tutar</label>
                <Input
                  type="number"
                  placeholder="0"
                  value={filters.minAmount}
                  onChange={(e) => setFilters(prev => ({...prev, minAmount: e.target.value}))}
                />
              </div>

              {/* Max Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max. Tutar</label>
                <Input
                  type="number"
                  placeholder="∞"
                  value={filters.maxAmount}
                  onChange={(e) => setFilters(prev => ({...prev, maxAmount: e.target.value}))}
                />
              </div>

              {/* Project */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Proje</label>
                <Input
                  type="text"
                  placeholder="Proje ara..."
                  value={filters.project}
                  onChange={(e) => setFilters(prev => ({...prev, project: e.target.value}))}
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
                <Input
                  type="text"
                  placeholder="Kategori ara..."
                  value={filters.category}
                  onChange={(e) => setFilters(prev => ({...prev, category: e.target.value}))}
                />
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Requests Table */}
      <Card>
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Tüm Avans Talepleri</h2>
        </div>

        <div className="overflow-x-auto hidden md:block">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sıra No
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avans No
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Talep Tarihi
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Talep Eden
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ödenen
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kapatılan
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fark
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Durum
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRequests.map((request) => {
                // Calculate paid and closed amounts (use ORIGINAL amounts, not converted)
                const isPaid = ['paid', 'finance_pending', 'closed'].includes(request.status);
                const isClosed = request.status === 'closed';
                
                const paidAmount = isPaid 
                  ? (request.approved_amount || request.amount)
                  : 0;
                  
                const closedAmount = isClosed && request.closed_amount_original
                  ? request.closed_amount_original
                  : 0;
                
                // Calculate difference (closed - paid)
                const difference = closedAmount - paidAmount;
                const diffColor = difference < 0 ? 'text-red-600' : (difference > 0 ? 'text-green-600' : 'text-gray-600');
                const diffPrefix = difference < 0 ? '-' : (difference > 0 ? '+' : '');
                
                return (
                  <tr key={request.serial_no} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {request.serial_no}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                      <Tooltip content={request.advance_number}>
                        <span className="font-mono cursor-help hover:text-blue-700 transition-colors">
                          {formatAdvanceNumber(request.advance_number)}
                        </span>
                      </Tooltip>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <Tooltip content={formatDateTime(request.request_date)}>
                        <span className="cursor-help">
                          {request.request_date ? formatDate(request.request_date) : '-'}
                        </span>
                      </Tooltip>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                      {request.requester_name}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-gray-900 font-medium">
                      {formatCurrency(paidAmount, request.currency)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-gray-900 font-medium">
                      {formatCurrency(closedAmount, request.currency)}
                    </td>
                    <td className={`px-4 py-4 whitespace-nowrap text-sm text-center font-medium ${diffColor}`}>
                      {isClosed ? `${diffPrefix}${formatCurrency(Math.abs(difference), request.currency)}` : '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-center">
                      {getStatusBadge(request)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile card view */}
        <div className="md:hidden divide-y divide-gray-100" data-testid="all-requests-mobile-list">
          {filteredRequests.map((request) => {
            const isPaid = ['paid', 'finance_pending', 'closed'].includes(request.status);
            const isClosed = request.status === 'closed';
            const paidAmount = isPaid ? (request.approved_amount || request.amount) : 0;
            const closedAmount = isClosed && request.closed_amount_original
              ? request.closed_amount_original : 0;
            const difference = closedAmount - paidAmount;
            const diffColor = difference < 0 ? 'text-red-600' : (difference > 0 ? 'text-green-600' : 'text-gray-600');
            const diffPrefix = difference < 0 ? '-' : (difference > 0 ? '+' : '');
            return (
              <div key={request.serial_no} className="p-4" data-testid={`all-req-card-${request.serial_no}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="text-xs text-gray-500">#{request.serial_no} • Avans</div>
                    <Tooltip content={request.advance_number}>
                      <div className="font-mono text-sm font-semibold text-blue-600 break-all">
                        {formatAdvanceNumber(request.advance_number)}
                      </div>
                    </Tooltip>
                  </div>
                  <div className="flex-shrink-0">{getStatusBadge(request)}</div>
                </div>
                <div className="text-xs text-gray-600 mb-2">
                  <span className="font-medium">{request.requester_name}</span>
                  {request.request_date && (
                    <span className="text-gray-400"> • {formatDate(request.request_date)}</span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="bg-gray-50 rounded p-2">
                    <div className="text-gray-500">Ödenen</div>
                    <div className="font-medium text-gray-900 tabular-nums">
                      {formatCurrency(paidAmount, request.currency)}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded p-2">
                    <div className="text-gray-500">Kapatılan</div>
                    <div className="font-medium text-gray-900 tabular-nums">
                      {formatCurrency(closedAmount, request.currency)}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded p-2">
                    <div className="text-gray-500">Fark</div>
                    <div className={`font-medium tabular-nums ${diffColor}`}>
                      {isClosed ? `${diffPrefix}${formatCurrency(Math.abs(difference), request.currency)}` : '—'}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Table Footer Summary */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Toplam {requestsData.total_count} talep • Son güncelleme: {new Date().toLocaleString('tr-TR')}
            </div>
            <div className="text-sm font-medium text-gray-900">
              <CalendarIcon className="w-4 h-4 inline mr-1" />
              Rolünüz: {requestsData.user_role}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AllRequests;